import React from 'react';
import { Draggable } from 'react-beautiful-dnd';

import { DragHandle } from 'src/svg/drag-handle';
import { GqlImageBanner } from 'types/graphql';

import { ListItemScheduleInfo } from 'src/components/schedule-form';
import { MoreButton, Option } from 'src/components/table-more-button';

import { hasScheduleBegun } from 'shared/helpers/schedule';
import { useUI } from 'src/hooks/use-ui';
import {
  Container,
  ImageContainer,
  Image,
  Controls,
  IconContainer,
  DragHandleContainer,
  LinksAndSchedule,
} from './image-banner-list-item.styles';
import { ImageBannerLinks } from './image-banner-links';

type ImageBannerListItemProps = {
  banner: GqlImageBanner;
  hasMobileApp: boolean;
  index: number;
  onRemove: (index: number) => void;
  onEdit: (id: string) => void;
  onCopyToLocations?: (banner: GqlImageBanner) => void;
  isActive?: boolean;
  showCopyToLocations?: boolean;
  /** Name of the location this banner was copied from (if applicable) */
  copiedFromLocation?: string;
};

export const TEST_ID_IMAGE_BANNER_ITEM = 'image-banner-item';
export const TEST_ID_IMAGE_BANNER_ITEM_REMOVE = 'image-banner-item-remove';
export const TEST_ID_IMAGE_BANNER_ITEM_EDIT = 'image-banner-item-edit';
export const TEST_ID_IMAGE_BANNER_ITEM_COPY = 'image-banner-item-copy';

const imgixParams = {
  crop: 'edges',
  fit: 'crop',
  h: 63,
  w: 140,
};

export function ImageBannerListItem({
  banner,
  hasMobileApp,
  index,
  onRemove,
  onEdit,
  isActive,
}: ImageBannerListItemProps): JSX.Element {
  const { _id, image, mobileImage, alt } = banner;
  const imageSource = mobileImage.includes('://') ? mobileImage : image;
  const { dispensary } = useUI();
  const isLive = hasScheduleBegun(banner.schedule, dispensary.timezone ?? 'UTC');

  return (
    <Draggable draggableId={_id} index={index}>
      {(provided) => (
        <Container
          {...provided.draggableProps}
          ref={provided.innerRef}
          data-testid={TEST_ID_IMAGE_BANNER_ITEM}
          isActive={isActive ?? true}
        >
          <>
            <ImageContainer>
              <Image imgixParams={imgixParams} src={imageSource} disableSrcSet htmlAttributes={{ alt }} />
            </ImageContainer>
            <LinksAndSchedule>
              <ImageBannerLinks banner={banner} hasMobileApp={hasMobileApp} />
              {banner.schedule && (
                <ListItemScheduleInfo schedule={banner.schedule} isLive={isLive} isActive={isActive ?? true} />
              )}
            </LinksAndSchedule>
          </>
          <Controls>
            <IconContainer>
              <MoreButton>
                <Option onClick={() => onEdit(_id)} data-testid={TEST_ID_IMAGE_BANNER_ITEM_EDIT}>
                  Edit
                </Option>
                <Option onClick={() => onRemove(index)} data-testid={TEST_ID_IMAGE_BANNER_ITEM_REMOVE}>
                  Delete
                </Option>
              </MoreButton>
            </IconContainer>
            <DragHandleContainer {...provided.dragHandleProps}>
              <DragHandle />
            </DragHandleContainer>
          </Controls>
        </Container>
      )}
    </Draggable>
  );
}
